import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from '@environment';
import { AuthData } from '../../models/auth-data.model';
import { UserRole } from '../../models/user-role.model';
import { AuthDataBE } from '../../models/auth-data-be.model';
import { ChangePasswordErrorBE } from '../../models/change-password-error-be.model';
import { ChangePasswordFieldsError } from '../../models/change-password-fields-error.model';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  constructor(private readonly httpClient: HttpClient) {}

  login(email: string, password: string): Observable<AuthData> {
    const url = `${environment.apiEnv}api/rest-auth/login/`;
    return this.httpClient
      .post<AuthDataBE>(url, { email, password })
      .pipe(map(authDataBE => this.transformIntoAuthData(authDataBE)));
  }

  sendResetPasswordMail(email: string): Observable<boolean> {
    const url = `${environment.apiEnv}api/rest-auth/password/reset/`;
    return this.httpClient.post(url, { email }).pipe(
      switchMap(() => of(true)),
      catchError(() => of(false)),
    );
  }

  resetPassword(uid: string, token: string, password: string, passwordConfirm: string): Observable<boolean> {
    const url = `${environment.apiEnv}api/rest-auth/password/reset/confirm/`;
    return this.httpClient.post(url, { uid, token, new_password1: password, new_password2: passwordConfirm }).pipe(
      switchMap(() => of(true)),
      catchError(() => of(false)),
    );
  }

  // A 400 becomes a ChangePasswordFieldsError the form shows field by field; anything else passes through.
  changePassword$(newPassword: string, confirmNewPassword: string): Observable<void> {
    const url = `${environment.apiEnv}api/rest-auth/password/change/`;
    return this.httpClient.post(url, { new_password1: newPassword, new_password2: confirmNewPassword }).pipe(
      map(() => undefined),
      catchError((error: unknown) =>
        throwError(() => (error instanceof HttpErrorResponse ? this.transformIntoChangePasswordError(error) : error)),
      ),
    );
  }

  checkToken(uid: string, token: string): Observable<boolean> {
    const url = `${environment.apiEnv}api/rest-auth/password/reset/check/`;
    return this.httpClient.post(url, { uid, token }).pipe(
      switchMap(() => of(true)),
      catchError(() => of(false)),
    );
  }

  transformIntoAuthData(authDataBE: AuthDataBE): AuthData {
    return {
      brand: authDataBE.brand,
      language: authDataBE.preferred_language,
      redirectUrl: this.getRedirectUrl(authDataBE.redirect_url),
      role: authDataBE.is_staff ? UserRole.STAFF : UserRole.MEMBER,
      token: authDataBE.key,
    };
  }

  private transformIntoChangePasswordError(error: HttpErrorResponse): ChangePasswordFieldsError | HttpErrorResponse {
    if (error.status !== HttpStatusCode.BadRequest || !error.error) {
      return error;
    }
    const changePasswordErrorBE: ChangePasswordErrorBE = error.error;
    // The body is only as typed as the backend makes it, so a non-array value is skipped.
    const newPasswordMessages = Array.isArray(changePasswordErrorBE.new_password1)
      ? changePasswordErrorBE.new_password1
      : [];
    const confirmNewPasswordMessages = Array.isArray(changePasswordErrorBE.new_password2)
      ? changePasswordErrorBE.new_password2
      : [];
    if (!newPasswordMessages.length && !confirmNewPasswordMessages.length) {
      return error;
    }
    return new ChangePasswordFieldsError({
      ...(newPasswordMessages.length && { newPassword: newPasswordMessages }),
      ...(confirmNewPasswordMessages.length && { confirmNewPassword: confirmNewPasswordMessages }),
    });
  }

  private getRedirectUrl(redirectUrlBE: string | null): string | null {
    const redirectUrlInGroup1Regex = /https:\/\/.+?(\/.+)/;
    const [, group1] = redirectUrlBE?.match(redirectUrlInGroup1Regex) ?? [];
    return group1 ?? null;
  }
}
